import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Menu, Popover, Tooltip, useOverlayContext } from 'seagull';
import { PlusCircle } from 'seagull/icons';
import { generateUniqueId } from 'seagull/utils/generateUniqueId';
import { getTestIds } from 'seagull/utils/TestIds';
import { CustomPageTemplatesModal } from '~/components/CustomPageTemplates/CustomPageTemplatesModal/CustomPageTemplatesModal';
import { useShowSelectCustomPageTypeModal } from '~/components/SelectCustomPageTypeModal/SelectCustomPageTypeModal';
import { useStore } from '~/contexts';
import { useSaveCustomPageText } from '~/hooks/reactQuery/mutations/useSaveCustomPageText';
import { useCustomPageTemplates } from '~/hooks/reactQuery/queries/useCustomPageTemplates';
import { useFilePicker } from '~/hooks/useFilePicker';
import { useUploadCustomPagePdf } from '~/hooks/useUploadCustomPagePdf';
import { useLocaleKeys } from '~/i18n';
import { createBlankVideoPage, createBlankTextPage, createBlankPdfPage } from '~/models/custom-page/model';
import { selectors } from '~/store/state-selectors';
import sectionStyles from '../CustomPagesSection.module.scss';
const AMENDMENT_ALLOWED_TYPES = [
    'pdf',
    'text'
];
const DEFAULT_ALLOWED_TYPES = [
    'pdf',
    'text',
    'video'
];
export const GetAddCustomPageDropdownTID = (parentTestId)=>getTestIds(parentTestId);
export const AddCustomPageDropdown = (param)=>{
    let { testId, nextDisplayOrder, onAddPage, isMaxReached, getSourceId, onUploadingChange, amendmentMode = false } = param;
    const { t } = useLocaleKeys();
    const testIds = useMemo(()=>GetAddCustomPageDropdownTID(testId), [
        testId
    ]);
    const businessId = useStore(selectors.user.businessId);
    const { templates, isLoading } = useCustomPageTemplates({
        refreshOnHookMount: true
    });
    const { showModal, showErrorToast } = useOverlayContext();
    const showSelectTypeModal = useShowSelectCustomPageTypeModal();
    const { saveText } = useSaveCustomPageText();
    const { handleFileSelected: handlePdfFileSelected, isUploading } = useUploadCustomPagePdf({
        onSuccess: (param)=>{
            let { pdfName, pdfFileName } = param;
            onAddPage(createBlankPdfPage(generateUniqueId('custom-page'), nextDisplayOrder, pdfName, pdfFileName), {
                initialExpanded: true
            });
        }
    });
    useEffect(()=>{
        onUploadingChange(isUploading);
    }, [
        isUploading,
        onUploadingChange
    ]);
    const [isOpen, setIsOpen] = useState(false);
    const [anchorPosition, setAnchorPosition] = useState({
        top: 0,
        left: 0
    });
    const hasTemplates = templates.length > 0;
    const handleClose = useCallback(()=>{
        setIsOpen(false);
    }, []);
    const { openFilePicker: openPdfFilePicker, fileInputElement: pdfFileInputElement } = useFilePicker({
        accept: '.pdf',
        onFileSelected: handlePdfFileSelected
    });
    const handleCreateBlank = useCallback(async ()=>{
        await showSelectTypeModal(async (type)=>{
            const id = generateUniqueId('custom-page');
            if (type === 'video') {
                onAddPage(createBlankVideoPage(id, nextDisplayOrder), {
                    initialExpanded: true
                });
            } else if (type === 'text') {
                onAddPage(createBlankTextPage(id, nextDisplayOrder, ''), {
                    initialExpanded: true
                });
            } else if (type === 'pdf') {
                openPdfFilePicker();
            }
        }, {
            allowedTypes: amendmentMode ? AMENDMENT_ALLOWED_TYPES : DEFAULT_ALLOWED_TYPES
        });
        handleClose();
    }, [
        amendmentMode,
        handleClose,
        showSelectTypeModal,
        onAddPage,
        nextDisplayOrder,
        openPdfFilePicker
    ]);
    const handleSelectTemplate = useCallback(async (template)=>{
        handleClose();
        const id = generateUniqueId('custom-page');
        const displayOrder = nextDisplayOrder;
        const baseFields = {
            id,
            pageTitle: template.pageTitle,
            displayOrder,
            displayScope: template.displayScope
        };
        const addOptions = {
            initialExpanded: false
        };
        switch(template.type){
            case 'video':
                onAddPage(_object_spread_props(_object_spread({}, baseFields), {
                    type: 'video',
                    video: {
                        videoUrl: template.video.videoUrl
                    }
                }), addOptions);
                break;
            case 'pdf':
                onAddPage(_object_spread_props(_object_spread({}, baseFields), {
                    type: 'pdf',
                    pdf: {
                        pdfName: template.pdf.pdfName,
                        pdfFileName: template.pdf.pdfFileName
                    }
                }), addOptions);
                break;
            case 'text':
                {
                    const sourceId = getSourceId();
                    if (businessId == null || sourceId === '') {
                        showErrorToast(t.customPages.templates.loadContentError());
                        return;
                    }
                    let parsedContent;
                    try {
                        parsedContent = JSON.parse(template.text.content);
                    } catch (unused) {
                        showErrorToast(t.customPages.templates.loadContentError());
                        return;
                    }
                    try {
                        const { textId } = await saveText({
                            businessId,
                            textId: '',
                            sourceId,
                            content: parsedContent
                        });
                        onAddPage(_object_spread_props(_object_spread({}, baseFields), {
                            type: 'text',
                            text: {
                                textId
                            }
                        }), addOptions);
                    } catch (unused) {
                        showErrorToast(t.customPages.templates.loadContentError());
                    }
                    break;
                }
        }
    }, [
        handleClose,
        nextDisplayOrder,
        onAddPage,
        saveText,
        businessId,
        getSourceId,
        showErrorToast,
        t
    ]);
    const handleManageTemplates = useCallback(async ()=>{
        await showModal({
            biName: 'custom_page_templates_modal',
            openBottomSheetOnMobile: true,
            hideBottomSheetOnDismiss: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(CustomPageTemplatesModal, {
                    dismissModal: dismissModal
                });
            }
        });
    }, [
        showModal
    ]);
    const handleAddPageClick = useCallback(()=>{
        if (hasTemplates || isLoading) {
            setAnchorPosition({
                top: window.innerHeight / 2,
                left: window.innerWidth / 2
            });
            setIsOpen(true);
        } else {
            handleCreateBlank();
        }
    }, [
        hasTemplates,
        isLoading,
        handleCreateBlank
    ]);
    const pageTypeLabel = useMemo(()=>({
            text: t.customPages.text(),
            pdf: t.customPages.pdf(),
            video: t.customPages.video()
        }), [
        t
    ]);
    const menuItems = useMemo(()=>templates.map((tmpl)=>{
            const isVideoLockedInAmendmentMode = amendmentMode && tmpl.type === 'video';
            return {
                id: tmpl.id,
                testId: testIds.AddCustomPageDropdownTID.TemplateItem(tmpl.id),
                title: tmpl.name,
                body: pageTypeLabel[tmpl.type],
                bi: {
                    name: 'select_custom_page_template',
                    data: {
                        template_id: tmpl.id
                    }
                },
                onClick: ()=>handleSelectTemplate(tmpl),
                disabled: isVideoLockedInAmendmentMode,
                tooltip: isVideoLockedInAmendmentMode ? t.customPages.amendments.cannotAddVideo() : undefined
            };
        }), [
        templates,
        handleSelectTemplate,
        amendmentMode,
        testIds,
        t,
        pageTypeLabel
    ]);
    const addButton = /*#__PURE__*/ _jsxs("button", {
        type: "button",
        className: sectionStyles.addPageSlot,
        "data-testid": testIds.AddCustomPageDropdownTID.AddButton(),
        onClick: isMaxReached ? undefined : handleAddPageClick,
        disabled: isMaxReached,
        children: [
            /*#__PURE__*/ _jsx(PlusCircle, {
                size: 16
            }),
            t.customPages.addCustomPage()
        ]
    });
    return /*#__PURE__*/ _jsxs(Box, {
        testId: testIds.AddCustomPageDropdownTID.Root(),
        children: [
            pdfFileInputElement,
            /*#__PURE__*/ _jsx(Tooltip, {
                title: isMaxReached ? t.customPages.maxPagesReached() : '',
                children: /*#__PURE__*/ _jsx("span", {
                    style: {
                        display: 'block',
                        width: '100%'
                    },
                    children: addButton
                })
            }),
            (hasTemplates || isLoading) && /*#__PURE__*/ _jsx(Popover, {
                open: isOpen,
                anchorReference: "anchorPosition",
                anchorPosition: anchorPosition,
                anchorOrigin: {
                    vertical: 'center',
                    horizontal: 'center'
                },
                transformOrigin: {
                    vertical: 'center',
                    horizontal: 'center'
                },
                onClose: handleClose,
                children: /*#__PURE__*/ _jsx(Menu, {
                    testId: testIds.AddCustomPageDropdownTID.Menu(),
                    dimensions: {
                        height: 400
                    },
                    loading: isLoading,
                    emptyText: t.customPages.dropdownNoMatches(),
                    searchProps: {
                        showSearch: true,
                        autoFocus: true,
                        bi: {
                            name: 'custom_page_template_search'
                        },
                        strategy: {
                            type: 'text'
                        }
                    },
                    items: menuItems,
                    footer: {
                        items: [
                            {
                                variant: 'contextualAction',
                                props: {
                                    title: t.customPages.addNew(),
                                    icon: /*#__PURE__*/ _jsx(PlusCircle, {}),
                                    testId: testIds.AddCustomPageDropdownTID.CreateBlank(),
                                    onClick: handleCreateBlank,
                                    bi: {
                                        name: 'create_blank_custom_page'
                                    }
                                }
                            },
                            {
                                variant: 'promotionalAction',
                                props: {
                                    title: t.customPages.manageTemplates(),
                                    body: t.customPages.manageTemplatesBody(),
                                    testId: testIds.AddCustomPageDropdownTID.ManageTemplates(),
                                    onClick: handleManageTemplates,
                                    bi: {
                                        name: 'manage_custom_page_templates'
                                    }
                                }
                            }
                        ]
                    }
                })
            })
        ]
    });
};
